import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteNotifications, useMarkAsRead, useMarkAllAsRead } from "@/hooks/useNotifications";
import { useGetUserPreferences } from "@/hooks/useUserPreferences";
import { Spinner } from "@/components/Spinner";
import { IoTimeOutline, IoCheckmarkDoneOutline } from "react-icons/io5";
import { formatDateTime } from "@/utils/formatDateTime";
import { getTimeAgo } from "@/utils/getTimeAgo";

export const Alerts = () => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: preferences } = useGetUserPreferences();
  
  // Se todas as notificações estão desabilitadas, buscar apenas antigas
  const allDisabled = !preferences?.notifyStatusUpdates && 
                      !preferences?.notifyCityNews && 
                      !preferences?.notifyGovernmentReplies && 
                      !preferences?.notifyNeighborhoodNews;
  
  const { 
    data, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage,
    isLoading,
  } = useInfiniteNotifications(allDisabled);
  
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  // Intersection Observer para scroll infinito
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Flatten das páginas
  const allNotifications = data?.pages.flatMap(page => page.data) || [];
  const unreadCount = allNotifications.filter(n => !n.isRead).length;

  const handleMarkAsRead = (notificationId: string) => {
    markAsRead.mutate([notificationId]);
  };

  const handleNotificationClick = (notification: any) => {
    // Se não está lida, marcar como lida
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    
    // Navegar para detalhes de ocorrência
    const occurrenceTypes = ['CITY_HALL_RESPONSE', 'OCCURRENCE_UPDATE', 'OCCURRENCE_STATUS_CHANGE'];
    if (occurrenceTypes.includes(notification.type) && notification.occurrenceId) {
      navigate(`/ocurrences/details/${notification.occurrenceId}`);
      return;
    }
    
    // Navegar para detalhes de ordem de serviço
    const workOrderTypes = ['WORK_ORDER_ASSIGNED', 'WORK_ORDER_UPDATE', 'WORK_ORDER_STATUS_CHANGE'];
    if (workOrderTypes.includes(notification.type) && notification.workOrderId) {
      navigate(`/work-orders/${notification.workOrderId}`);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "OCCURRENCE_UPDATE":
        return "📋";
      case "OCCURRENCE_STATUS_CHANGE":
        return "🔄";
      case "NEW_COMMENT":
        return "💬";
      case "SYSTEM":
        return "⚙️";
      default:
        return "🔔";
    }
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen pt-20 pb-20 overflow-x-hidden">
      <div className="flex items-center justify-center mt-4">
        <div className="lg:w-[98%] w-[95%] max-w-4xl">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-800">Notificações</h1>
          </div>

          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-600 text-sm font-medium">
              {unreadCount} {unreadCount === 1 ? 'não lida' : 'não lidas'}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={markAllAsRead.isPending}
                className="text-primary font-semibold cursor-pointer text-sm hover:underline disabled:opacity-50"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="w-8 h-8" />
            </div>
          ) : allNotifications.length > 0 ? (
            <div className="flex flex-col gap-3">
              {allNotifications.map((notification, index) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.5) }}
                >
                  <div
                    className={`bg-white rounded-xl p-4 shadow-sm border transition-all cursor-pointer hover:shadow-md ${
                      notification.isRead 
                        ? 'border-gray-200' 
                        : 'border-primary/30 bg-primary/5'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xl">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {notification.title}
                          </h3>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2 break-words">
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                          <div className="flex items-center gap-1">
                            <IoTimeOutline />
                            <span>{getTimeAgo(notification.createdAt)}</span>
                          </div>
                          {notification.isRead && notification.readAt && (
                            <div className="flex items-center gap-1">
                              <IoCheckmarkDoneOutline className="text-green-600" />
                              <span>Lida em {formatDateTime(notification.readAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {/* Elemento observer para scroll infinito */}
              <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
                {isFetchingNextPage && (
                  <Spinner className="w-6 h-6" />
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <div className="text-5xl mb-3">🔔</div>
              <p className="text-gray-600 font-medium">
                Nenhuma notificação
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
